import { PropsWithChildren, ReactNode, useState } from 'react'

import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import { useCreation } from 'ahooks'
import { Popconfirm } from 'antd'

import { GenericalRecord } from '@eastcoal/common/types/utilities'

import { useCreationForm } from '../../hooks/useCreationForm'
import { SchemaForm } from '../SchemaForm'

import { CrudPopFormType, CrudPopformProps } from './interface'

const TYPE_ICON_MAP: Record<CrudPopFormType, ReactNode> = {
  info: <InfoCircleOutlined />,
  success: <CheckCircleOutlined />,
  warning: <ExclamationCircleOutlined />
}

const DEFAULT_GAP = {
  columns: 1
}

export const CrudPopform = <FormData extends GenericalRecord>({
  /* -------------------------------------------------------------------------- */
  /*                              CrudPopform Props                             */
  /* -------------------------------------------------------------------------- */
  width = 320,
  api,
  onSubmit,
  onSuccess,

  /* -------------------------------------------------------------------------- */
  /*                            useCreationForm Porps                           */
  /* -------------------------------------------------------------------------- */
  display,
  effects,
  initialValues,
  mergeStrategy,
  pattern,
  validateFirst,
  values,

  /* -------------------------------------------------------------------------- */
  /*                              SchemaForm Props                              */
  /* -------------------------------------------------------------------------- */
  name,
  schema,
  layout,
  grid = DEFAULT_GAP,
  context,

  /* -------------------------------------------------------------------------- */
  /*                              Popconfirm Props                              */
  /* -------------------------------------------------------------------------- */
  title,
  disabled,
  placement = 'left',
  type,
  cancelButtonProps,
  cancelText,
  okButtonProps,
  okText = '提交',
  okType,
  children
}: PropsWithChildren<CrudPopformProps<FormData>>) => {
  const [open, setOpen] = useState(false)

  const handleConfirm = async () => {
    return form.submit(async values => {
      const formData = onSubmit?.(values) ?? values
      const detailData = await api(values)
      onSuccess?.(Object.assign({}, formData, detailData))
    })
  }

  const handleCancel = () => {
    setOpen(false)
  }

  const handleAfterOpenChange = () => {
    if (!open) {
      form.reset()
    }
  }

  const form = useCreationForm({
    display,
    effects,
    initialValues,
    mergeStrategy,
    pattern,
    validateFirst,
    values
  })

  const expressionScope = useCreation(
    () => ({ $context: context ?? {} }),
    [context]
  )

  const formDom = (
    <SchemaForm
      form={form}
      grid={grid}
      layout={layout}
      name={name}
      schema={schema}
      scope={expressionScope}
      style={{ width }}
    />
  )

  return (
    <Popconfirm
      afterOpenChange={handleAfterOpenChange}
      cancelButtonProps={cancelButtonProps}
      cancelText={cancelText}
      description={formDom}
      disabled={disabled}
      icon={TYPE_ICON_MAP[type]}
      okButtonProps={okButtonProps}
      okText={okText}
      okType={okType}
      open={open}
      placement={placement}
      title={title}
      trigger='click'
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      onOpenChange={setOpen}
    >
      {children}
    </Popconfirm>
  )
}
